import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client.js'
import { useAuth } from '../context/AuthContext.jsx'
import { useToast } from '../context/ToastContext.jsx'
import ItemCard from '../components/ItemCard.jsx'
import WeaponArt from '../components/WeaponArt.jsx'
import CoinIcon from '../components/CoinIcon.jsx'
import { formatRub, formatChance, RARITY_COLORS, RARITY_LABELS, WEAR_SHORT } from '../lib/format.js'
import { playTickSound, playWinSound, preloadSounds } from '../lib/sound.js'
import '../styles/picker.css'
import '../styles/upgrade.css'

const SPIN_MS = 3400
const SPIN_TURNS = 4

/**
 * Множители-пресеты (кнопки быстрых действий).
 */
const MULT_PRESETS = [1.5, 2, 3, 5, 10, 20]
const UPGRADE_RTP = 0.92
const PAGE_SIZE = 12

/**
 * Пагинатор по 12 скинов на страницу.
 */
function Pagination({ page, totalPages, onPageChange }) {
  if (totalPages <= 1) return null

  return (
    <div className="picker-pagination">
      <button
        type="button"
        className="pagination-btn pagination-arrow"
        onClick={() => onPageChange(Math.max(1, page - 1))}
        disabled={page <= 1}
        title="Предыдущая страница"
        aria-label="Предыдущая страница"
      >
        ‹
      </button>
      <button
        type="button"
        className="pagination-btn pagination-arrow"
        onClick={() => onPageChange(Math.min(totalPages, page + 1))}
        disabled={page >= totalPages}
        title="Следующая страница"
        aria-label="Следующая страница"
      >
        ›
      </button>
    </div>
  )
}

/**
 * Поиск цели, чей множитель ближе всего к выбранному значению.
 * Ищет точный минимум разницы по всему массиву без преждевременных прерываний.
 */
function nearestByMultiplier(targets, multiplier, stakePriceKopecks = 0) {
  if (!targets || targets.length === 0) return null
  let best = null
  let minDiff = Infinity

  for (const entry of targets) {
    const val = entry.priceKopecks ?? entry.valueKopecks ?? 0
    let m = entry.multiplier
    if (!m || m <= 0) {
      m = stakePriceKopecks > 0 ? val / stakePriceKopecks : 0
    }
    if (stakePriceKopecks > 0) {
      if (m < 1.15) continue
      const ch = stakePriceKopecks / val
      if (ch < 0.005 || ch > 0.85) continue
    }
    const diff = Math.abs(m - multiplier)
    if (diff < minDiff) {
      minDiff = diff
      best = entry
    }
  }
  return best || targets[0]
}

/**
 * Крупная карточка на сцене апгрейда: слева — свой предмет, справа — цель.
 * Оформлена в стиле карточек содержимого кейса: центрированная, со свечением и водяным знаком.
 */
function StageCard({ side, label, item, priceKopecks, hint, placeholder }) {
  const rarity = item?.rarity

  return (
    <div
      className={`upgrade-card upgrade-card-${side} ${item ? '' : 'is-empty'}`}
      style={{ '--rarity': rarity ? RARITY_COLORS[rarity] : '#8b5cf6' }}
    >
      <span className="upgrade-card-tag">{label}</span>

      <div className="upgrade-card-badges-top-right">
        {item?.wear && (
          <span className="upgrade-card-wear-badge">{WEAR_SHORT[item.wear] ?? item.wear}</span>
        )}
        {item?.stattrak && <span className="upgrade-card-st-badge">ST</span>}
      </div>

      {item ? (
        <>
          <span className="upgrade-card-art">
            <WeaponArt image={item.image} rarity={rarity} title={item.skin} size="lg" />
          </span>

          <div className="upgrade-card-meta">
            <span className="upgrade-card-weapon">{item.name}</span>
            <span className="upgrade-card-skin">{item.skin}</span>
          </div>

          <div className="upgrade-card-foot">
            <span className="upgrade-card-price">
              <span>{formatRub(priceKopecks)}</span>
              <CoinIcon size="sm" />
            </span>
          </div>
        </>
      ) : (
        <>
          <span className="upgrade-card-slot" aria-hidden="true">
            {placeholder}
          </span>
          <span className="upgrade-card-hint">{hint}</span>
        </>
      )}
    </div>
  )
}

export default function Upgrade() {
  const { user, setBalance } = useAuth()
  const toast = useToast()

  const [inventory, setInventory] = useState(null)
  const [stakeItemId, setStakeItemId] = useState(null)

  const [targets, setTargets] = useState(null)
  const [stakeKopecks, setStakeKopecks] = useState(0)
  const [targetId, setTargetId] = useState(null)
  const [loadingTargets, setLoadingTargets] = useState(false)

  // Фильтры двух списков и выбранный множитель (по умолчанию x2)
  const [invSearchMode, setInvSearchMode] = useState('price')
  const [invQuery, setInvQuery] = useState('')
  const [invMinPrice, setInvMinPrice] = useState('')
  const [invMaxPrice, setInvMaxPrice] = useState('')
  const [invSort, setInvSort] = useState('new')
  const [invPage, setInvPage] = useState(1)

  const [targetSearchMode, setTargetSearchMode] = useState('price')
  const [query, setQuery] = useState('')
  const [targetMinPrice, setTargetMinPrice] = useState('')
  const [targetMaxPrice, setTargetMaxPrice] = useState('')
  const [sort, setSort] = useState('chance')
  const [targetPage, setTargetPage] = useState(1)
  const [preset, setPreset] = useState(2)

  const [spinning, setSpinning] = useState(false)
  const [angle, setAngle] = useState(0)
  const [result, setResult] = useState(null)
  const [round, setRound] = useState(null)

  const timer = useRef(null)

  useEffect(() => {
    preloadSounds().catch(() => {})
    return () => clearTimeout(timer.current)
  }, [])

  useEffect(() => {
    setInvPage(1)
  }, [invSearchMode, invQuery, invMinPrice, invMaxPrice, invSort])

  useEffect(() => {
    setTargetPage(1)
  }, [targetSearchMode, query, targetMinPrice, targetMaxPrice, sort, stakeItemId])

  // Загрузка инвентаря пользователя
  useEffect(() => {
    if (user) {
      api.inventory().then(({ items }) => setInventory(items)).catch(() => setInventory([]))
    } else {
      setInventory([])
    }
  }, [user])

  const stakeItem = useMemo(
    () => (inventory ?? []).find((item) => item.id === stakeItemId) ?? null,
    [inventory, stakeItemId],
  )

  // Загрузка доступных целей под выбранную ставку (или всего каталога, если ставка не выбрана)
  const loadTargetsForStake = useCallback(
    async (inventoryId, preferredMultiplier) => {
      setLoadingTargets(true)
      try {
        const data = await api.upgradeTargets(inventoryId ? { inventoryId } : {})
        setTargets(data.targets)
        setStakeKopecks(data.stakeKopecks ?? 0)
        setTargetPage(1)

        setTargetId((prevTargetId) => {
          if (prevTargetId && data.targets?.some((t) => t.targetId === prevTargetId)) {
            return prevTargetId
          }
          if (inventoryId && data.targets && data.targets.length > 0) {
            const multToUse = preferredMultiplier ?? preset ?? 2
            const best = nearestByMultiplier(data.targets, multToUse, data.stakeKopecks ?? 0)
            return best ? best.targetId : data.targets[0].targetId
          }
          return null
        })
      } catch (err) {
        setTargets([])
        setStakeKopecks(0)
        setTargetId(null)
        setTargetPage(1)
        toast.error(err.message || 'Не удалось загрузить цели')
        if (inventoryId) {
          api.inventory().then(({ items }) => setInventory(items)).catch(() => {})
        }
      } finally {
        setLoadingTargets(false)
      }
    },
    [preset, toast],
  )

  // При открытии страницы загружаем каталог целей, чтобы он работал сразу
  useEffect(() => {
    loadTargetsForStake(null)
  }, [loadTargetsForStake])

  const target = useMemo(() => {
    return (targets ?? []).find((t) => t.targetId === targetId) ?? null
  }, [targets, targetId])

  /** Инвентарь после поиска и сортировки */
  const visibleInventory = useMemo(() => {
    const list = inventory ?? []
    let found = list

    if (invSearchMode === 'name') {
      const needle = invQuery.trim().toLowerCase()
      if (needle) {
        found = list.filter((item) => `${item.name} ${item.skin}`.toLowerCase().includes(needle))
      }
    } else {
      const minVal = invMinPrice.trim() !== '' ? parseFloat(invMinPrice.replace(',', '.')) : null
      const maxVal = invMaxPrice.trim() !== '' ? parseFloat(invMaxPrice.replace(',', '.')) : null
      const hasMin = minVal !== null && !Number.isNaN(minVal)
      const hasMax = maxVal !== null && !Number.isNaN(maxVal)

      if (hasMin || hasMax) {
        const minKop = hasMin ? Math.round(minVal * 100) : -Infinity
        const maxKop = hasMax ? Math.round(maxVal * 100) : Infinity
        found = list.filter((item) => {
          const kop = item.priceKopecks ?? 0
          return kop >= minKop && kop <= maxKop
        })
      }
    }

    return [...found].sort((a, b) => {
      if (invSort === 'rich') return b.priceKopecks - a.priceKopecks
      if (invSort === 'cheap') return a.priceKopecks - b.priceKopecks
      return b.id - a.id
    })
  }, [inventory, invSearchMode, invQuery, invMinPrice, invMaxPrice, invSort])

  const totalInvPages = Math.max(1, Math.ceil(visibleInventory.length / PAGE_SIZE))
  const currentInvPage = Math.min(invPage, totalInvPages)
  const pagedInventory = useMemo(() => {
    const start = (currentInvPage - 1) * PAGE_SIZE
    return visibleInventory.slice(start, start + PAGE_SIZE)
  }, [visibleInventory, currentInvPage])

  /** Цели после поиска и сортировки (без дубликатов) */
  const visibleTargets = useMemo(() => {
    const list = targets ?? []
    const seen = new Set()
    const unique = []

    const isNameMode = targetSearchMode === 'name'
    const needle = isNameMode ? query.trim().toLowerCase() : ''

    const minVal = !isNameMode && targetMinPrice.trim() !== '' ? parseFloat(targetMinPrice.replace(',', '.')) : null
    const maxVal = !isNameMode && targetMaxPrice.trim() !== '' ? parseFloat(targetMaxPrice.replace(',', '.')) : null
    const hasMin = minVal !== null && !Number.isNaN(minVal)
    const hasMax = maxVal !== null && !Number.isNaN(maxVal)
    const minKop = hasMin ? Math.round(minVal * 100) : -Infinity
    const maxKop = hasMax ? Math.round(maxVal * 100) : Infinity

    for (const entry of list) {
      const key = `${entry.name}|${entry.skin}|${entry.wear || 'none'}|${entry.stattrak ? 'st' : 'norm'}`.toLowerCase()
      if (seen.has(key)) continue
      seen.add(key)

      if (isNameMode) {
        if (needle && !`${entry.name} ${entry.skin}`.toLowerCase().includes(needle)) {
          continue
        }
      } else {
        const val = entry.priceKopecks ?? entry.valueKopecks ?? 0
        if (val < minKop || val > maxKop) {
          continue
        }
      }

      unique.push(entry)
    }

    return unique.sort((a, b) => {
      const valA = a.priceKopecks ?? a.valueKopecks ?? 0
      const valB = b.priceKopecks ?? b.valueKopecks ?? 0
      if (sort === 'rich') return valB - valA
      if (sort === 'chance') {
        const diff = b.chance - a.chance
        if (diff !== 0) return diff
        return valA - valB
      }
      return valA - valB
    })
  }, [targets, targetSearchMode, query, targetMinPrice, targetMaxPrice, sort])

  const totalTargetPages = Math.max(1, Math.ceil(visibleTargets.length / PAGE_SIZE))
  const currentTargetPage = Math.min(targetPage, totalTargetPages)
  const pagedTargets = useMemo(() => {
    const start = (currentTargetPage - 1) * PAGE_SIZE
    return visibleTargets.slice(start, start + PAGE_SIZE)
  }, [visibleTargets, currentTargetPage])

  const canPlay = Boolean(stakeItem) && Boolean(target) && !spinning

  // Выбор цели вручную
  function pickTarget(entry) {
    setPreset(null)
    setResult(null)
    if (entry.targetId === targetId) {
      setTargetId(null)
      return
    }
    setTargetId(entry.targetId)
  }

  // Выбор своего скина из инвентаря
  function pickStake(item) {
    setResult(null)
    setRound(null)

    if (item.id === stakeItemId) {
      setStakeItemId(null)
      setStakeKopecks(0)
      loadTargetsForStake(null)
      return
    }

    setStakeItemId(item.id)
    const priceKop = item.priceKopecks ?? 0
    setStakeKopecks(priceKop)

    // Справа цель оставляем, если она подходит под новую ставку; иначе подбираем новую
    if (targets && targets.length > 0) {
      const curTarget = (targets ?? []).find((t) => t.targetId === targetId)
      const curTargetPrice = curTarget?.priceKopecks ?? curTarget?.valueKopecks ?? 0
      const isValidForNewStake =
        curTarget &&
        curTargetPrice > 0 &&
        priceKop > 0 &&
        curTargetPrice / priceKop >= 1.15 &&
        priceKop / curTargetPrice >= 0.005 &&
        priceKop / curTargetPrice <= 0.85

      if (!isValidForNewStake) {
        const best = nearestByMultiplier(targets, preset ?? 2, priceKop)
        if (best) {
          setTargetId(best.targetId)
          const idx = visibleTargets.findIndex((t) => t.targetId === best.targetId)
          if (idx !== -1) {
            setTargetPage(Math.floor(idx / PAGE_SIZE) + 1)
          }
        }
      }
    }

    loadTargetsForStake(item.id, preset)
  }

  // Нажатие на кнопку быстрого множителя (×1.5, ×2, ×3, ×5, ×10, ×20)
  function handleSelectPreset(value) {
    setPreset(value)
    setResult(null)

    const curStakePrice = stakeItem?.priceKopecks ?? stakeKopecks ?? 0
    if (targets && targets.length > 0) {
      const best = nearestByMultiplier(targets, value, curStakePrice)
      if (best) {
        setTargetId(best.targetId)
        const idx = visibleTargets.findIndex((t) => t.targetId === best.targetId)
        if (idx !== -1) {
          setTargetPage(Math.floor(idx / PAGE_SIZE) + 1)
        }
      }
    }
  }

  // Запуск апгрейда
  async function play() {
    if (!canPlay) return

    setResult(null)
    setSpinning(true)
    const snapshot = { stake: stakeItem, target }

    try {
      const data = await api.upgradeExecute({
        inventoryId: stakeItemId,
        targetId: target.targetId,
        targetItemId: target.itemId,
        wear: target.wear,
        stattrak: target.stattrak,
      })

      // Запуск вращения стрелки
      const landing = SPIN_TURNS * 360 + data.fair.roll * 360
      setAngle((current) => current + landing - (current % 360))

      // Звуки вращения рулетки
      const tickTimers = []
      let t = 0
      let step = 45
      while (t < SPIN_MS - 300) {
        const at = t
        tickTimers.push(
          setTimeout(() => {
            playTickSound(0.18)
          }, at),
        )
        const progress = t / SPIN_MS
        if (progress > 0.6) step += 25
        t += step
      }

      timer.current = setTimeout(() => {
        setSpinning(false)
        setRound(snapshot)
        setResult(data)
        setBalance(data.balance)

        if (data.won) {
          playWinSound(0.38)
          toast.success(`Апгрейд удался! ${data.drop.name} | ${data.drop.skin}`)
        } else {
          toast.info('Не в этот раз')
        }

        // Мгновенно синхронизируем локальный инвентарь (убираем сгоревший, добавляем выигранный)
        setInventory((current) => {
          const updated = (current ?? []).filter((it) => it.id !== snapshot.stake.id)
          if (data.won && data.drop) {
            updated.unshift({
              id: data.drop.inventoryId,
              itemId: data.drop.itemId,
              name: data.drop.name,
              skin: data.drop.skin,
              rarity: data.drop.rarity,
              rarityLabel: data.drop.rarityLabel || '',
              rarityColor: data.drop.rarityColor || '',
              weaponType: data.drop.weaponType,
              image: data.drop.image,
              wear: data.drop.wear,
              wearLabel: data.drop.wearLabel,
              wearShort: data.drop.wearShort,
              floatValue: data.drop.floatValue,
              stattrak: data.drop.stattrak,
              priceKopecks: data.drop.priceKopecks,
              status: 'owned',
            })
          }
          return updated
        })

        // Фоновая проверка инвентаря с бэкенда
        api.inventory().then(({ items }) => setInventory(items)).catch(() => {})

        // Сбрасываем выбранный предмет после раунда
        setStakeItemId(null)
      }, SPIN_MS)
    } catch (err) {
      setSpinning(false)
      toast.error(err.message || 'Ошибка выполнения апгрейда')
    }
  }

  /* Во время крутки показываем ставку раунда; после завершения отыгранный скин слева тупо пропадает, а цель справа остаётся */
  const shownStake = spinning ? (round?.stake ?? stakeItem) : stakeItem
  const shownTarget = spinning ? (round?.target ?? target) : target

  const stakePrice = shownStake?.priceKopecks ?? stakeKopecks ?? 0
  const targetPrice = shownTarget?.priceKopecks ?? shownTarget?.valueKopecks ?? 0

  const chance = useMemo(() => {
    if (result) return round?.target?.chance ?? 0
    if (shownTarget?.chance && shownTarget.chance > 0) return shownTarget.chance
    if (stakePrice > 0 && targetPrice > 0) {
      return Math.min(0.85, Math.max(0.005, stakePrice / targetPrice))
    }
    return 0
  }, [result, round, shownTarget, stakePrice, targetPrice])

  const multiplier = useMemo(() => {
    if (result) return round?.target?.multiplier ?? 0
    if (shownTarget?.multiplier && shownTarget.multiplier > 0) return shownTarget.multiplier
    if (stakePrice > 0 && targetPrice > 0) {
      return targetPrice / stakePrice
    }
    return 0
  }, [result, round, shownTarget, stakePrice, targetPrice])

  const stageStyle = {
    '--chance-deg': `${chance * 360}deg`,
    '--rarity': shownTarget ? RARITY_COLORS[shownTarget.rarity] : 'var(--ink-200)',
  }
  const stageState = result ? (result.won ? 'is-win' : 'is-lose') : ''

  return (
    <div className="page container upgrade-page">
      {/* ── Барабан: отдаёшь → шанс → получаешь ──────────────── */}
      <section className={`upgrade-stage ${stageState}`} style={stageStyle}>
        <div className="stage-row">
          <StageCard
            side="stake"
            label="Отдаёшь"
            item={shownStake}
            priceKopecks={shownStake?.priceKopecks}
            hint="Выбери свой предмет"
            placeholder="+"
          />

          <div className="dial-wrap">
            <div className={`upgrade-dial ${spinning ? 'is-spinning' : ''}`}>
              <span className="dial-halo" aria-hidden="true" />
              <span className="dial-track" aria-hidden="true" />
              <span className="dial-fill" aria-hidden="true" />
              <span className="dial-edge" aria-hidden="true" />
              <span className="dial-ticks" aria-hidden="true" />
              <span className="dial-hub" aria-hidden="true" />
              <span
                className="dial-arrow"
                aria-hidden="true"
                style={{
                  transform: `rotate(${angle}deg)`,
                  transition: spinning
                    ? `transform ${SPIN_MS}ms cubic-bezier(0.14, 0.72, 0.16, 1)`
                    : 'none',
                }}
              />

              <div className="dial-face">
                {result ? (
                  <>
                    <span className={`dial-verdict ${result.won ? 'is-win' : 'is-lose'}`}>
                      {result.won ? 'Успех' : 'Мимо'}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="dial-chance">
                      {shownTarget && stakeItem && chance > 0 ? formatChance(chance) : '—'}
                    </span>
                    <span className="dial-cap">шанс</span>
                    {shownTarget && stakeItem && multiplier > 0 && (
                      <span className="dial-mult">×{multiplier.toFixed(2)}</span>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          <StageCard
            side="target"
            label="Получаешь"
            item={shownTarget}
            priceKopecks={shownTarget?.priceKopecks ?? shownTarget?.valueKopecks}
            hint="Выбери цель"
            placeholder="?"
          />
        </div>

        <div className="stage-bar">
          {/* Кнопки быстрых действий (Множители) */}
          <div className="mult-row" role="group" aria-label="Множитель">
            {MULT_PRESETS.map((value) => (
              <button
                key={value}
                type="button"
                className={`mult-btn ${preset === value ? 'is-active' : ''}`}
                onClick={() => handleSelectPreset(value)}
                disabled={spinning}
                title={`Подобрать цель с множителем ×${value}`}
              >
                ×{value}
              </button>
            ))}
          </div>

          {!user ? (
            <a
              href="/api/auth/steam"
              className="btn btn-primary btn-lg upgrade-go btn-steam-action"
            >
              <svg className="steam-icon" viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                <path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658c.545-.371 1.203-.59 1.912-.59.063 0 .125.004.188.006l2.861-4.142V8.91c0-2.495 2.028-4.524 4.524-4.524 2.494 0 4.524 2.031 4.524 4.527s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.005.105.005.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.727L.436 15.27C1.862 20.307 6.486 24 11.979 24c6.627 0 12-5.373 12-12S18.605 0 11.979 0z" />
              </svg>
              <span>Войти через Steam</span>
            </a>
          ) : (
            <button
              type="button"
              className="btn btn-primary btn-lg upgrade-go"
              onClick={play}
              disabled={!canPlay}
            >
              {spinning
                ? 'Крутим…'
                : !stakeItem
                  ? 'Выбери свой предмет'
                  : !target
                    ? 'Выбери цель'
                    : 'Улучшить'}
            </button>
          )}
        </div>
      </section>

      {/* ── Выбор: свой инвентарь и цели ─────────────────────── */}
      <div className="upgrade-cols">
        <section className="card picker-panel">
          <div className="panel-head">
            <h2>Твои предметы</h2>
            <div className="panel-head-actions">
              {inventory && <span className="tiny muted">{visibleInventory.length} шт.</span>}
              {totalInvPages > 1 && (
                <div className="panel-head-nav">
                  <button
                    type="button"
                    className="panel-nav-btn"
                    onClick={() => setInvPage((p) => Math.max(1, p - 1))}
                    disabled={currentInvPage <= 1}
                    title="Предыдущая страница"
                    aria-label="Предыдущая страница"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    className="panel-nav-btn"
                    onClick={() => setInvPage((p) => Math.min(totalInvPages, p + 1))}
                    disabled={currentInvPage >= totalInvPages}
                    title="Следующая страница"
                    aria-label="Следующая страница"
                  >
                    ›
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="panel-tools">
            <div className="panel-filter-group">
              <button
                type="button"
                className={`panel-search-toggle ${invSearchMode === 'name' ? 'is-active' : ''}`}
                onClick={() => setInvSearchMode((m) => (m === 'price' ? 'name' : 'price'))}
                title={invSearchMode === 'price' ? 'Поиск по названию' : 'Фильтр по цене'}
                aria-label={invSearchMode === 'price' ? 'Поиск по названию' : 'Фильтр по цене'}
              >
                <svg className="panel-search-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </button>

              {invSearchMode === 'name' ? (
                <input
                  type="search"
                  className="panel-search"
                  placeholder="Поиск по названию"
                  value={invQuery}
                  onChange={(event) => setInvQuery(event.target.value)}
                  aria-label="Поиск по инвентарю"
                  autoFocus
                />
              ) : (
                <div className="panel-price-range">
                  <input
                    type="text"
                    inputMode="decimal"
                    className="panel-price-input"
                    placeholder="От ₽"
                    value={invMinPrice}
                    onChange={(event) =>
                      setInvMinPrice(event.target.value.replace(/[^\d.,]/g, '').slice(0, 9))
                    }
                    aria-label="Цена от"
                  />
                  <span className="panel-price-dash">—</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="panel-price-input"
                    placeholder="До ₽"
                    value={invMaxPrice}
                    onChange={(event) =>
                      setInvMaxPrice(event.target.value.replace(/[^\d.,]/g, '').slice(0, 9))
                    }
                    aria-label="Цена до"
                  />
                </div>
              )}
            </div>
            <div className="seg seg-sm">
              <button
                type="button"
                className={`seg-btn ${invSort === 'new' ? 'is-active' : ''}`}
                onClick={() => setInvSort('new')}
              >
                Новые
              </button>
              <button
                type="button"
                className={`seg-btn ${invSort === 'rich' ? 'is-active' : ''}`}
                onClick={() => setInvSort('rich')}
              >
                Дороже
              </button>
              <button
                type="button"
                className={`seg-btn ${invSort === 'cheap' ? 'is-active' : ''}`}
                onClick={() => setInvSort('cheap')}
              >
                Дешевле
              </button>
            </div>
          </div>

          {!user ? (
            <div className="panel-empty">
              <p className="muted">Войди через Steam, чтобы выбрать скин для апгрейда.</p>
              <a href="/api/auth/steam" className="btn btn-steam btn-sm">
                <svg className="steam-icon" viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                  <path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658c.545-.371 1.203-.59 1.912-.59.063 0 .125.004.188.006l2.861-4.142V8.91c0-2.495 2.028-4.524 4.524-4.524 2.494 0 4.524 2.031 4.524 4.527s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.005.105.005.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.727L.436 15.27C1.862 20.307 6.486 24 11.979 24c6.627 0 12-5.373 12-12S18.605 0 11.979 0z" />
                </svg>
                <span>Войти через Steam</span>
              </a>
            </div>
          ) : inventory === null ? (
            <div className="skeleton" style={{ height: 260, margin: 18 }} />
          ) : inventory.length === 0 ? (
            <div className="panel-empty">
              <p className="muted">Инвентарь пуст — апгрейдить пока нечего.</p>
              <Link to="/cases" className="btn btn-soft btn-sm">
                Открыть кейс
              </Link>
            </div>
          ) : visibleInventory.length === 0 ? (
            <p className="panel-empty muted">По запросу ничего не нашлось.</p>
          ) : (
            <>
              <div className="picker-grid">
                {pagedInventory.map((item) => (
                  <ItemCard
                    key={item.id}
                    item={item}
                    size="sm"
                    selected={item.id === stakeItemId}
                    onClick={() => pickStake(item)}
                    disabled={spinning}
                  />
                ))}
              </div>
              <Pagination
                page={currentInvPage}
                totalPages={totalInvPages}
                onPageChange={setInvPage}
              />
            </>
          )}
        </section>

        <section className="card picker-panel">
          <div className="panel-head">
            <h2>ЦЕЛЬ</h2>
            {totalTargetPages > 1 && (
              <div className="panel-head-actions">
                <div className="panel-head-nav">
                  <button
                    type="button"
                    className="panel-nav-btn"
                    onClick={() => setTargetPage((p) => Math.max(1, p - 1))}
                    disabled={currentTargetPage <= 1}
                    title="Предыдущая страница"
                    aria-label="Предыдущая страница"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    className="panel-nav-btn"
                    onClick={() => setTargetPage((p) => Math.min(totalTargetPages, p + 1))}
                    disabled={currentTargetPage >= totalTargetPages}
                    title="Следующая страница"
                    aria-label="Следующая страница"
                  >
                    ›
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="panel-tools">
            <div className="panel-filter-group">
              <button
                type="button"
                className={`panel-search-toggle ${targetSearchMode === 'name' ? 'is-active' : ''}`}
                onClick={() => setTargetSearchMode((m) => (m === 'price' ? 'name' : 'price'))}
                title={targetSearchMode === 'price' ? 'Поиск по названию' : 'Фильтр по цене'}
                aria-label={targetSearchMode === 'price' ? 'Поиск по названию' : 'Фильтр по цене'}
              >
                <svg className="panel-search-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </button>

              {targetSearchMode === 'name' ? (
                <input
                  type="search"
                  className="panel-search"
                  placeholder="Поиск по названию"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  aria-label="Поиск по целям"
                  autoFocus
                />
              ) : (
                <div className="panel-price-range">
                  <input
                    type="text"
                    inputMode="decimal"
                    className="panel-price-input"
                    placeholder="От ₽"
                    value={targetMinPrice}
                    onChange={(event) =>
                      setTargetMinPrice(event.target.value.replace(/[^\d.,]/g, '').slice(0, 9))
                    }
                    aria-label="Цена от"
                  />
                  <span className="panel-price-dash">—</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="panel-price-input"
                    placeholder="До ₽"
                    value={targetMaxPrice}
                    onChange={(event) =>
                      setTargetMaxPrice(event.target.value.replace(/[^\d.,]/g, '').slice(0, 9))
                    }
                    aria-label="Цена до"
                  />
                </div>
              )}
            </div>
            <div className="seg seg-sm">
              <button
                type="button"
                className={`seg-btn ${sort === 'chance' ? 'is-active' : ''}`}
                onClick={() => setSort('chance')}
              >
                Шанс выше
              </button>
              <button
                type="button"
                className={`seg-btn ${sort === 'rich' ? 'is-active' : ''}`}
                onClick={() => setSort('rich')}
              >
                Дороже
              </button>
            </div>
          </div>

          {loadingTargets ? (
            <div className="skeleton" style={{ height: 260, margin: 18 }} />
          ) : !targets || targets.length === 0 ? (
            <p className="panel-empty muted">
              {stakeItem
                ? 'Для этого предмета нет подходящих целей — он слишком дорогой для каталога.'
                : 'Цели не загрузились.'}
            </p>
          ) : visibleTargets.length === 0 ? (
            <p className="panel-empty muted">По запросу ничего не нашлось.</p>
          ) : (
            <>
              <div className="picker-grid">
                {pagedTargets.map((entry) => (
                  <ItemCard
                    key={entry.targetId}
                    item={entry}
                    size="sm"
                    selected={entry.targetId === targetId}
                    onClick={() => pickTarget(entry)}
                    disabled={spinning}
                    footer={
                      stakeItem && stakeKopecks > 0 ? (() => {
                        const val = entry.priceKopecks ?? entry.valueKopecks ?? 0
                        const m = entry.multiplier > 0 ? entry.multiplier : (val > 0 ? val / stakeKopecks : 0)
                        const ch = entry.chance > 0 ? entry.chance : (val > 0 ? stakeKopecks / val : 0)
                        if (m < 1.15 || ch < 0.005 || ch > 0.85) return null
                        return (
                          <div className="target-card-odds">
                            <span className="target-mult">×{m.toFixed(2)}</span>
                            <span className="target-chance">{formatChance(ch)}</span>
                          </div>
                        )
                      })() : null
                    }
                  />
                ))}
              </div>
              <Pagination
                page={currentTargetPage}
                totalPages={totalTargetPages}
                onPageChange={setTargetPage}
              />
            </>
          )}
        </section>
      </div>
    </div>
  )
}
